<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { api, type AccountPayload, type OnboardingInput } from "../api";
import { authClient, authErrorMessage } from "../auth-client";
import TokenManager from "./TokenManager.vue";

type AuthSession = {
  token: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  expiresAt: Date | string;
  ipAddress?: string | null;
  userAgent?: string | null;
};

const props = defineProps<{ account: AccountPayload }>();
const emit = defineEmits<{ saved: [account: AccountPayload]; error: [message: string]; toast: [message: string]; signedOut: [] }>();
const profile = reactive<OnboardingInput>({
  display_name: props.account.athlete.display_name,
  timezone: props.account.athlete.timezone,
  unit_system: props.account.athlete.unit_system,
  training_days_per_week: props.account.onboarding.training_days_per_week ?? 4,
  session_minutes: props.account.onboarding.session_minutes ?? 55,
  experience_level: props.account.onboarding.experience_level ?? "intermediate",
  goals: props.account.onboarding.goals,
  available_equipment: props.account.onboarding.available_equipment,
  movement_constraints: props.account.onboarding.movement_constraints,
  variety_preference: props.account.onboarding.variety_preference
});
const newEmail = ref(props.account.user.email);
const currentPassword = ref("");
const newPassword = ref("");
const sessions = ref<AuthSession[]>([]);
const profileBusy = ref(false);
const securityBusy = ref(false);
const deletePhrase = ref("");
const deleteRequested = ref(false);

function fail(caught: unknown, fallback: string) {
  if (caught && typeof caught === "object" && "message" in caught) emit("error", String(caught.message));
  else emit("error", fallback);
}

async function loadSessions() {
  const result = await authClient.listSessions();
  if (result.error) emit("error", authErrorMessage(result.error, "Could not load active sessions."));
  else sessions.value = result.data ?? [];
}

async function saveProfile() {
  profileBusy.value = true;
  try {
    const authResult = await authClient.updateUser({ name: profile.display_name });
    if (authResult.error) throw authResult.error;
    const account = await api.saveOnboarding(profile);
    emit("saved", account);
    emit("toast", "Training preferences saved");
  } catch (caught) { fail(caught, "Could not save training preferences."); }
  finally { profileBusy.value = false; }
}

async function changeEmail() {
  securityBusy.value = true;
  const result = await authClient.changeEmail({ newEmail: newEmail.value, callbackURL: `${location.origin}/?auth=email-changed` });
  if (result.error) emit("error", authErrorMessage(result.error, "Could not start the email change."));
  else emit("toast", "Check your email to confirm the change");
  securityBusy.value = false;
}

async function changePassword() {
  securityBusy.value = true;
  const result = await authClient.changePassword({
    currentPassword: currentPassword.value,
    newPassword: newPassword.value,
    revokeOtherSessions: true
  });
  if (result.error) emit("error", authErrorMessage(result.error, "Could not change the password."));
  else {
    currentPassword.value = "";
    newPassword.value = "";
    emit("toast", "Password changed and other sessions signed out");
    await loadSessions();
  }
  securityBusy.value = false;
}

async function revokeOthers() {
  securityBusy.value = true;
  const result = await authClient.revokeOtherSessions();
  if (result.error) emit("error", authErrorMessage(result.error, "Could not revoke other sessions."));
  else { emit("toast", "Other sessions signed out"); await loadSessions(); }
  securityBusy.value = false;
}

async function requestDeletion() {
  if (deletePhrase.value !== "DELETE") return;
  securityBusy.value = true;
  const result = await authClient.deleteUser({ callbackURL: `${location.origin}/?auth=deleted` });
  if (result.error) emit("error", authErrorMessage(result.error, "Could not request account deletion."));
  else deleteRequested.value = true;
  securityBusy.value = false;
}

function deviceLabel(value?: string | null): string {
  if (!value) return "Unknown device";
  if (/iPhone|iPad/i.test(value)) return "iPhone or iPad";
  if (/Android/i.test(value)) return "Android device";
  if (/Macintosh/i.test(value)) return "Mac";
  if (/Windows/i.test(value)) return "Windows PC";
  return "Web browser";
}

onMounted(() => { void loadSessions(); });
</script>

<template>
  <section class="view settings-view">
    <header class="view-header"><div><p class="eyebrow">Account & coaching context</p><h1>Settings</h1></div><span class="live-chip"><i></i>Verified</span></header>

    <div class="settings-layout">
      <form class="settings-card settings-profile" @submit.prevent="saveProfile">
        <header><div><p class="eyebrow">Athlete</p><h2>Training preferences</h2></div><span>Used by plans + agents</span></header>
        <div class="form-grid">
          <label>Name<input v-model="profile.display_name" required maxlength="100"></label>
          <label>Time zone<input v-model="profile.timezone" required maxlength="80"></label>
          <label>Units<select v-model="profile.unit_system"><option value="lb">Pounds</option><option value="kg">Kilograms</option></select></label>
          <label>Experience<select v-model="profile.experience_level"><option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option></select></label>
          <label>Days / week<input v-model.number="profile.training_days_per_week" type="number" min="1" max="7" required></label>
          <label>Workout minutes<input v-model.number="profile.session_minutes" type="number" min="15" max="180" step="5" required></label>
          <label class="wide">Exercise variety<select v-model="profile.variety_preference"><option value="steady">Keep movements steady</option><option value="balanced">Rotate every few weeks</option><option value="frequent">Frequent substitutions</option></select></label>
          <label class="wide">Goals<textarea v-model="profile.goals" rows="3" maxlength="2000" required></textarea></label>
          <label class="wide">Equipment<textarea v-model="profile.available_equipment" rows="2" maxlength="2000" required></textarea></label>
          <label class="wide">Movement constraints<textarea v-model="profile.movement_constraints" rows="2" maxlength="2000"></textarea></label>
        </div>
        <button class="button primary" :disabled="profileBusy">{{ profileBusy ? "Saving…" : "Save preferences" }}</button>
      </form>

      <section class="settings-card">
        <header><div><p class="eyebrow">Security</p><h2>Login & recovery</h2></div><b>{{ account.user.email }}</b></header>
        <form class="inline-security-form" @submit.prevent="changeEmail"><label>Change email<input v-model="newEmail" type="email" required></label><button class="button ghost" :disabled="securityBusy || newEmail === account.user.email">Send confirmation</button></form>
        <form class="inline-security-form password-form" @submit.prevent="changePassword">
          <label>Current password<input v-model="currentPassword" type="password" autocomplete="current-password" required></label>
          <label>New password<input v-model="newPassword" type="password" autocomplete="new-password" minlength="10" maxlength="128" required></label>
          <button class="button ghost" :disabled="securityBusy">Change password</button>
        </form>
      </section>

      <section class="settings-card">
        <header><div><p class="eyebrow">Devices</p><h2>Active sessions</h2></div><button class="text-button" :disabled="securityBusy || sessions.length < 2" @click="revokeOthers">Sign out others</button></header>
        <div class="session-list"><article v-for="session in sessions" :key="session.token"><span>◉</span><div><b>{{ deviceLabel(session.userAgent) }}</b><small>{{ session.ipAddress || "Private IP" }} · active {{ new Date(session.updatedAt).toLocaleDateString() }}</small></div></article><p v-if="!sessions.length" class="muted">Loading sessions…</p></div>
      </section>

      <section class="settings-card agent-settings">
        <header><div><p class="eyebrow">Planning agents</p><h2>Personal API access</h2></div></header>
        <TokenManager @error="emit('error', $event)" />
      </section>

      <section class="settings-card danger-zone">
        <header><div><p class="eyebrow">Danger zone</p><h2>Delete account</h2></div></header>
        <p>Authentication access will be removed. Completed workout history is retained as disabled data rather than destructively erased.</p>
        <div v-if="deleteRequested" class="auth-message">Check your email for the final confirmation link.</div>
        <form v-else class="delete-form" @submit.prevent="requestDeletion"><label>Type DELETE to confirm<input v-model="deletePhrase" autocomplete="off"></label><button class="button" :disabled="securityBusy || deletePhrase !== 'DELETE'">Email deletion link</button></form>
      </section>
    </div>
  </section>
</template>
